import classNames from 'classnames';
import { endsWith, filter, find, forEach, groupBy, has, indexOf, keys, map, orderBy, size, some, sortBy, uniqBy } from 'lodash';
import { fromPairs, uniqueId } from 'lodash/fp';
import { MutableRefObject, ReactEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt, FaPrint } from 'react-icons/fa';
import { Redirect, useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Badge, Button, Nav, Navbar, NavbarBrand, NavItem as BsNavItem, NavLink } from 'reactstrap';
import { gql, useQuery } from 'urql';
import { CenteredContainer } from '.';
import styled from '../themes/themed-styled-components';
import { GraphDeepPartialOverride } from '../types';
import { EmergencyCard, HacpackQuery } from '../types/graph';
import Loading from './Loading';
import { GridLayout } from './ui';
import EmergencyCardViewerNew from './EmergencyCardViewer'

// @ts-ignore
type EmergencyCardsPageQueryData = GraphDeepPartialOverride<HacpackQuery, {
    emergencyCardAssetsBaseUrl: string,
    emergencyCards: EmergencyCard[]
}>

const LeftColumn = styled.div`
    height: 100%;
    min-width: 10em;
    overflow-y: auto;
    position: relative;
    background-color: ${({ theme: { gray200 } }) => gray200};
    border-right: ${({ theme: { borderWidth, borderColor } }) => `${borderWidth} ${borderColor} solid`};

    h6 {
        color:  ${({ theme: { gray600 } }) => gray600};
        background-color: ${({ theme: { gray100 } }) => gray100};
        margin: 0;
    }
    @media print {
        display: none;
    }
`;

const NavItem = styled(BsNavItem)`
    background-color: ${({ theme: { gray100 } }) => gray100};

    &.active {
        position: sticky;
        top: 0;
        bottom: 0;
        background-color: ${({ theme: { white } }) => white};
        box-shadow: ${({ theme: { boxShadowSm } }) => boxShadowSm}
    }

    a {
        display: flex;
        justify-content: space-between;
    }
`;

const EmergencyCardContainer = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
`;

const EmergencyCardIframe = styled.iframe`
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    overflow: auto;
    display: block;
    @media print {
        min-height: 100vh;
    }
`;

const EmergencyCardViewerOverlay = styled(CenteredContainer)`
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${({ theme: { white } }) => white};
`;

const emergencyCardsQuery = gql<EmergencyCardsPageQueryData>`
query {
    emergencyCardAssetsBaseUrl,
    emergencyCards {
        unNumber,
        hazardClass,
        url
    }
}`;

type EmergencyCardViewerProps = {
    emergencyCard: EmergencyCard
}

type EmergencyCardViewerError = 'notfound' | 'request' | 'server' | 'unknown';

const EmergencyCardViewer = ({ emergencyCard }: EmergencyCardViewerProps) => {
    const { url } = emergencyCard;
    const [iframeSrc, setIframeSrc] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<EmergencyCardViewerError | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>();

    const { t } = useTranslation('common');

    useEffect(() => {
        setLoading(true);
        setError(null);
        setIframeSrc('about:blank');

        let abort = false;

        const controller = new AbortController();
        const { signal } = controller;

        fetch(url as string, { signal }).then((res) => {


            if (abort === true) {
                return;
            }

            switch (res.status) {
                case 200:
                    setIframeSrc(url);
                    break;
                case 404:
                    setError('notfound');
                    setLoading(false);
                    break;
                case 500:
                    setError('server');
                    setLoading(false);
                    break;
                default:
                    setError('unknown');
                    setLoading(false);
            }
        }).catch((err) => {
            if (abort === true || err.name === "AbortError") {
                return;
            }
            setError('request');
        });

        return () => {
            controller.abort();
            abort = true;
        }
    }, [url, setLoading, setIframeSrc, setError]);



    const handleIframeAnchorClick = useCallback((ev: Event) => {
        const el = ev.target as HTMLElement;

        if (el.tagName.toLocaleUpperCase() === 'A') {
            const { href } = (el as HTMLAnchorElement);

            window.open(href);

            ev.preventDefault();
        }
    }, []);



    const handleIframeLoad: ReactEventHandler<HTMLIFrameElement> = useCallback((ev) => {

        const iframe = ev.target as HTMLIFrameElement;

        if (iframe.src === 'about:blank') {
            return;
        }


        //iframe.contentWindow?.document.body.addEventListener('click', handleIframeAnchorClick);
        const iframeDoc = iframe.contentWindow?.document;

        if (iframeDoc === undefined) {
            return;
        }

        const anchorsByUrl: Record<string, string> = {};

        const firstDiv = iframeDoc.createElement('div');
        //firstDiv.style.backgroundColor = iframeDoc.body.getAttribute('bgcolor');
        //iframeDoc.body.removeAttribute('bgcolor');

        const iframeHead = iframeDoc.head || iframeDoc.getElementsByTagName('head')[0];
        const styleEl = iframeDoc.createElement('style');
        styleEl.innerHTML = `
body > div { padding-bottom: 1em };
td { padding: 2em }
`;
        iframeHead.appendChild(styleEl);

        const aOnclick: EventListener = (evt: Event) => {
            const ev = evt as MouseEvent;

            if (ev.buttons !== 0 || ev.button !== 0) {
                return;
            }

            ev.preventDefault();
            const { href } = ev.currentTarget as HTMLAnchorElement;

            if (iframe.contentWindow === null) {
                return;
            }
            iframe.contentWindow.location.hash = `#`;
            iframe.contentWindow.location.hash = `#${anchorsByUrl[href]}`;
        };

        Array.from(iframeDoc.getElementsByTagName('a')).forEach((a) => {
            const { href } = a;

            if (!endsWith(href, '.htm') && !endsWith(href, '.html')) {
                return;
            }

            if (!has(anchorsByUrl, href)) {
                anchorsByUrl[href] = uniqueId('emc-');
            }

            //a.href = `#${anchorsByUrl[origA]}`;
            a.addEventListener('click', aOnclick);
        });

        firstDiv.appendChild.apply(firstDiv, [iframeDoc.body.children[0]]);
        iframeDoc.body.append(firstDiv);

        const parser = new DOMParser();

        Promise.all(map(anchorsByUrl, (id, url) => fetch(url).then(res => res.text()).then((txt) => [url, txt] as [string, string]))).then((pr) => {
            const resByUrl = fromPairs(pr);

            forEach(anchorsByUrl, async (id, url) => {
                const html = resByUrl[url];
                const { body } = parser.parseFromString(html, 'text/html');
                const div = iframeDoc.createElement('div');

                div.id = id;
                div.innerHTML = body.innerHTML;
                //div.style.backgroundColor = body.getAttribute('bgcolor');
                iframeDoc.body.appendChild(div);
            });

            setLoading(false);
        });

    }, [handleIframeAnchorClick, setLoading]);



    const overlayContent = useMemo(() => {

        if (loading === true) {
            return <Loading />
        }

        if (error === null) {
            return null;
        }

        switch (error) {
            case 'notfound':
                return <p>{t('Scheda non trovata.')}</p>;
            case 'request':
                return <>
                    <p>{t('Si è verificato un errore durante la richiesta.')}</p>
                    <Button onClick={() => setIframeSrc('about:blank')}>{t('Si è verificato un errore durante la richiesta')}</Button>
                </>
            default:
                return <p>{t('Si è verificato un errore.')}</p>;
        }

    }, [loading, error, t])

    const handlePrintButtonClick = useCallback(() => {
        iframeRef.current?.contentWindow?.print();
    }, []);

    const header = useMemo(() => {
        if (loading === true || error !== null) {
            return null;
        }

        return <Navbar className="navbar ng-white border-bottom box-shadow" light>
            <div className="d-sm-inline-flex flex-sm-row">

            </div>
            <div className="navbar-right d-sm-inline-flex flex-sm-row" style={{ gap: '.25em' }}>
                <Button onClick={handlePrintButtonClick}><FaPrint /> {t('Stampa')}</Button>
                <Button tag={'a'} href={url} title={t('Apri in un\'altra scheda').toString()} target="_blank">
                    <FaExternalLinkAlt /><span className="sr-only">{' '}{t('Apri in un\'altra scheda')}</span>
                </Button>
            </div>
        </Navbar>;

    }, [loading, error, t, url])

    return <GridLayout header={header}>
        <EmergencyCardContainer>
            {(overlayContent !== null) ? <EmergencyCardViewerOverlay>{overlayContent}</EmergencyCardViewerOverlay> : null}
            <EmergencyCardIframe ref={iframeRef as MutableRefObject<HTMLIFrameElement>} src={iframeSrc} onLoad={handleIframeLoad} />
        </EmergencyCardContainer>
    </GridLayout>;
}

const EmergencyCardsPage = () => {

    const { t } = useTranslation('common');

    const { unNumber: selectedUnNumber, hazardClass: selectedHazardClass } = useParams<{ unNumber?: string, hazardClass?: string }>();

    const { state } = useLocation<{ parentUrl: string }>();

    const { parentUrl } = state ?? {};

    const [result] = useQuery<EmergencyCardsPageQueryData>({
        query: emergencyCardsQuery
    });

    const { data, fetching/*, error*/ } = result;

    const emergencyCards = data?.emergencyCards;

    const [unSearch, setUnSearch] = useState<string>('');

    const unSearchInput = useMemo(() =>
        <input type="search" className="form-control" value={unSearch} placeholder={'Cerca Numero UN'} onChange={(ev) => setUnSearch(ev.target.value)} maxLength={4} />
        , [unSearch, setUnSearch])

    const header = useMemo(() => <Navbar className="navbar ng-white border-bottom box-shadow" light>
        <div className="d-sm-inline-flex flex-sm-row">
            <NavbarBrand tag={Link} className="text-nowrap mr-sm-3" to={`${parentUrl ?? ''}/${t('schede-di-emergenza')}`}>{t('Schede di Emergenza')}</NavbarBrand>
        </div>
        <div className="navbar-right d-sm-inline-flex flex-sm-row">
            {unSearchInput}
        </div>
    </Navbar>, [parentUrl, unSearchInput, t])

    const filteredEmergencyCards = useMemo(() => {
        if (emergencyCards === undefined) {
            return [];
        }

        if (unSearch.length === 0) {
            return emergencyCards;
        }

        return emergencyCards.filter(({ unNumber }) => unNumber?.includes(unSearch) ?? false);
    }, [emergencyCards, unSearch])

    const groupedEmergencyCards = useMemo(() => {
        return groupBy(sortBy(filteredEmergencyCards, ['hazardClass', 'unNumber']), 'hazardClass');
    }, [filteredEmergencyCards, unSearch]);

    const hazardClassNav = useMemo(() => {
        if (groupedEmergencyCards === null || size(groupedEmergencyCards) === 0) {
            return null;
        }

        const hazardClasses = orderBy(keys(groupedEmergencyCards), (cl) => cl.length === 1 ? `${cl}.0` : cl);

        if (hazardClasses.length === 1 && hazardClasses[0] !== selectedHazardClass) {
            return <Redirect to={`${parentUrl ?? ''}/${t('schede-di-emergenza')}/${hazardClasses[0]}`} />
        }

        if (selectedHazardClass !== undefined && indexOf(hazardClasses, selectedHazardClass) === -1) {
            return <Redirect to={`${parentUrl ?? ''}/${t('schede-di-emergenza')}`} />
        }

        return <LeftColumn>
            <GridLayout header={<h6 className='p-2'>{t('Classe')}</h6>}>
                <Nav vertical>
                    {map(hazardClasses, (hazardClass) => {
                        const emergencyCards = groupedEmergencyCards[hazardClass];
                        const to = `${parentUrl ?? ''}/${t('schede-di-emergenza')}/${hazardClass}`;
                        const active = selectedHazardClass === hazardClass;

                        return <NavItem key={hazardClass} className={classNames({ active })}>
                            <NavLink tag={Link} className="nav-link" to={to}>
                                <span>{hazardClass}</span> <span><Badge>{emergencyCards.length}</Badge></span>
                            </NavLink>
                        </NavItem>
                    })}
                </Nav>
            </GridLayout>
        </LeftColumn>;
    }, [groupedEmergencyCards, selectedHazardClass, parentUrl, t]);

    const unNumberNav = useMemo(() => {
        if (groupedEmergencyCards === null || selectedHazardClass === undefined) {
            return null;
        }

        const hazardClassEmergencyCards = groupedEmergencyCards[selectedHazardClass];

        if ((hazardClassEmergencyCards?.length??0) === 0) {
            return null;
        }

        if (selectedUnNumber !== undefined && !some(hazardClassEmergencyCards, ['unNumber', selectedUnNumber])) {
            return <Redirect to={`${parentUrl ?? ''}/${t('schede-di-emergenza')}/${selectedHazardClass}`} />
        }

        if (selectedUnNumber === undefined && hazardClassEmergencyCards?.length === 1) {
            return <Redirect to={`${parentUrl ?? ''}/${t('schede-di-emergenza')}/${selectedHazardClass}/${hazardClassEmergencyCards[0].unNumber}`} />
        }

        return <LeftColumn>
            <GridLayout header={<h6 className='p-2'>{t('Numero UN')}</h6>}>
                <Nav vertical>
                    {map(uniqBy(hazardClassEmergencyCards, 'unNumber'), ({ unNumber }) => {
                        const to = `${parentUrl ?? ''}/${t('schede-di-emergenza')}/${selectedHazardClass}/${unNumber}`;
                        const active = selectedUnNumber === unNumber;

                        return <NavItem key={`${selectedHazardClass}-${unNumber}`} className={classNames({ active })}>
                            <NavLink tag={Link} className="nav-link" to={to}>
                                <span>{unNumber}</span>
                            </NavLink>
                        </NavItem>
                    })}
                </Nav>
            </GridLayout>
        </LeftColumn>;
    }, [groupedEmergencyCards, parentUrl, selectedHazardClass, selectedUnNumber, t]);

    const content = useMemo(() => {

        if (emergencyCards === undefined) {
            return fetching === true ? <Loading /> : <CenteredContainer>{t('Caricamento delle schede non riuscito')}</CenteredContainer>;
        }

        if (filteredEmergencyCards.length === 0) {
            return selectedHazardClass === undefined && selectedUnNumber === undefined
                ? <CenteredContainer>{t('Nessuna scheda trovata.')}</CenteredContainer>
                : <Redirect to={`${parentUrl ?? ''}/${t('schede-di-emergenza')}`} />
        }

        if (selectedHazardClass === undefined) {
            return <CenteredContainer>{t('Seleziona una classe')}</CenteredContainer>;
        }

        const selectedEmergencyCard = selectedUnNumber === undefined ? undefined : find(emergencyCards, { unNumber: selectedUnNumber, hazardClass: selectedHazardClass });

        return <GridLayout left={unNumberNav}>
            {selectedEmergencyCard === undefined
                ? <CenteredContainer>{t('Seleziona un numero UN')}</CenteredContainer>
                : selectedEmergencyCard?.url === undefined
                    ? <CenteredContainer>{t('Scheda non trovata')}</CenteredContainer>
                    : <><EmergencyCardViewerNew key={selectedEmergencyCard.url} title={`Scheda di emergenza UN ${selectedUnNumber} classe ${selectedHazardClass}`} url={selectedEmergencyCard.url} />{/*<EmergencyCardViewer emergencyCard={selectedEmergencyCard} />*/}</>
            }
        </GridLayout>;
    }, [filteredEmergencyCards, emergencyCards, fetching, unNumberNav, selectedHazardClass, selectedUnNumber, t])



    return <GridLayout header={header} left={hazardClassNav}>{content}</GridLayout>;
}

export default EmergencyCardsPage;